import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  styled,
  Typography,
} from "~/common/mui";
import AddIcon from "@mui/icons-material/Add";
import { IMG_NO_RESULTS_OR_MATCHES } from "~/utils/url/staticResources";
import { useTranslation } from "react-i18next";
import { ServiceCasesTableV2, ServiceCaseTableV2Skeleton } from "./ServiceCasesTableV2";
import type { CaseDataObject } from "~/features/orders/orderDetails/components/ServiceCases/types";
import {
  NoServiceCaseContainer,
  NoServiceCaseImage,
  NoServiceCaseImageContainer,
} from "~/features/orders/common/components/v2/OrderAndQuoteSharedStyles";
import type { Dispatch, SetStateAction } from "react";

interface Props {
  onClose: () => void;
  loading: boolean;
  serviceCases: CaseDataObject[];
  onAddCaseClick?: () => void;
  userHasCreatedCase: boolean;
  setSnackbar: Dispatch<SetStateAction<{ open: boolean; message: string }>>;
}

const ServiceCaseListDialog = ({
  onClose,
  loading,
  serviceCases,
  onAddCaseClick,
  userHasCreatedCase,
  setSnackbar,
}: Props) => {
  const { t } = useTranslation();

  const renderServiceCaseList = () => {
    if (loading) {
      return <ServiceCaseTableV2Skeleton />;
    }
    if (serviceCases.length === 0) {
      return (
        <NoServiceCaseContainer>
          <Typography variant="headlineSmall">{t("serviceCases.list.dialog.noCaseFound")}</Typography>
          <NoServiceCaseImageContainer>
            <NoServiceCaseImage src={IMG_NO_RESULTS_OR_MATCHES} alt={t("serviceCases.list.dialog.noCaseFound.alt")} />
          </NoServiceCaseImageContainer>
        </NoServiceCaseContainer>
      );
    } else {
      return (
        <ServiceCasesTableV2
          cases={serviceCases}
          userHasCreatedCase={userHasCreatedCase}
          setSnackbar={setSnackbar}
          showTableOnly={true}
        />
      );
    }
  };

  return (
    <Dialog open={true} maxWidth="desktop-s">
      <DialogHeader onClose={onClose}>
        <DialogTitle>{t("serviceCases.list.dialog.title")}</DialogTitle>
      </DialogHeader>

      <DialogContent>{renderServiceCaseList()}</DialogContent>

      {onAddCaseClick && (
        <StyledDialogActions>
          <Button variant="text" startIcon={<AddIcon />} onClick={onAddCaseClick} disabled={loading}>
            {t("serviceCases.list.dialog.createCaseButton")}
          </Button>
          <Button variant="text" onClick={onClose}>
            {t("cancel")}
          </Button>
        </StyledDialogActions>
      )}
    </Dialog>
  );
};

export default ServiceCaseListDialog;

const StyledDialogActions = styled(DialogActions)({
  justifyContent: "space-between",
});
